'use client'

import Image from 'next/image'
import { twMerge } from 'tailwind-merge'

import { Listing } from '@/data/getListing'
import { Button } from '../Button'
import { ModalGalleryContext } from '../ModalGallery/GalleryContext'
import { ListingGalleryAsset, remapListingAssetsToGallery } from './listingImages'

interface StaggeredGalleryProps {
  className?: string
  start?: 'left' | 'right'
  listing: Listing
}

type ListingGalleryAssetOrButton = ListingGalleryAsset | { id: 'button'; kind: 'button' }

export function ListingStaggeredGallery({ className, start = 'left', listing }: StaggeredGalleryProps) {
  const [, sendToModalGallery] = ModalGalleryContext.useActor()

  const { floorplanIndex, galleryIndex, images, threeDTourIndex, videoIndex } = remapListingAssetsToGallery(listing)
  const ignoreIndexes = [floorplanIndex, threeDTourIndex, videoIndex]
  const staggeredItems: ListingGalleryAssetOrButton[] = []
  let index = galleryIndex
  while (index < images.length && staggeredItems.length < 4) {
    // Need to make a local copy of the index, otherwise somehow the callback refers to the last one.
    const diffIndex = Number(index)
    if (!ignoreIndexes.includes(index)) {
      staggeredItems.push({
        ...images[index],
        cb: () => {
          sendToModalGallery({ type: 'Activate gallery', index: diffIndex })
        },
        caption: '',
      })
    }
    index++
  }
  staggeredItems.push({ id: 'button', kind: 'button' })

  const oddEvenClassName =
    start === 'left'
      ? 'sm:w-1/2 sm:odd:pr-4 sm:even:self-end sm:even:pl-4 md:odd:pr-6 md:even:pl-6 lg:odd:pr-10 lg:even:pl-10'
      : 'sm:w-1/2 sm:even:pr-4 sm:odd:self-end sm:odd:pl-4 md:even:pr-6 md:odd:pl-6 lg:even:pr-10 lg:odd:pl-10'

  return (
    <div className={twMerge('flex w-full flex-col gap-4 sm:pb-10 md:pb-20', className)}>
      {staggeredItems.map((item) => (
        <div
          key={item.id}
          className={twMerge('relative w-full sm:even:-my-20', item.kind !== 'button' && oddEvenClassName)}
        >
          {item.kind === 'button' ? (
            <div className="mt-16 flex flex-1 justify-center px-8 sm:p-4 md:p-0 md:pt-20 lg:mt-32">
              <Button
                className="flex h-11 items-center bg-neutral-850 px-6 font-sans uppercase tracking-widest text-neutral-50"
                onClick={() => sendToModalGallery({ type: 'Activate gallery' })}
              >
                View Gallery
              </Button>
            </div>
          ) : (
            <>
              <div
                className={twMerge(
                  'relative flex-1',
                  item.kind !== 'video' && item.height > item.width ? 'aspect-photo-portrait' : 'aspect-video',
                )}
                onClick={item.cb}
              >
                <Image
                  src={item.url}
                  loading="lazy"
                  alt={item.alt ?? item.caption ?? ''}
                  fill
                  sizes="(max-width: 640px) 100vw, 50vw"
                  unoptimized
                  className="cursor-pointer object-cover drop-shadow-lg transform-gpu firefox:shadow-lg firefox:drop-shadow-none"
                />
                {item.icon && (
                  <div className="absolute right-4 top-4 rounded-full bg-white p-2">
                    <item.icon />
                  </div>
                )}
              </div>
              {item.caption && (
                <div className="relative bottom-0">
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-black/75 md:h-20">
                    <div className="absolute bottom-4 right-4 whitespace-pre-line font-display text-2xl uppercase leading-tight tracking-wider text-white sm:px-4 sm:text-4.5xl md:px-0">
                      {item.caption}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  )
}
